import {Checkbox, Select} from 'antd';
import {isString} from 'lodash-es';
import React, {useMemo, useState, ReactElement} from 'react';

import {SearchableDropdown} from 'components/form/inputs/reference-select/searchable-dropdown';

import {
    CustomSelectEntryHighlighter,
} from '../../components/custom-select-entry-highlighter/custom-select-entry-highlighter';
import {ICustomSelectEntry, ECustomSelectMode, ICustomSelectProps} from '../../custom-select';
import { TSortComparator } from '../../custom-select-utils';

interface ICustomSelectConfigProps<L, V> {
    mode?: ECustomSelectMode;
    showSearch?: boolean;
    entriesToRender: ICustomSelectEntry<L, V>[];
    formatFieldValueForSelect: (value: any) => string;
    optionsFilterCallback?: ICustomSelectProps<L, V>['optionsFilterCallback'];
    optionsLabelFormatter?: (label: L, value: V) => any;
    sortComparator?: TSortComparator<L, V>;
}

export const useCustomSelectConfig = <L, V>({
    mode,
    showSearch,
    entriesToRender,
    optionsFilterCallback,
    formatFieldValueForSelect,
    optionsLabelFormatter,
    sortComparator,
}: ICustomSelectConfigProps<L, V>) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [dropdownInputValue, setDropdownInputValue] = useState<string>('');

    const filterEntry = (input: string, option: any) => option?.children?.toLowerCase?.()
        .includes(input?.toLowerCase?.());

    const filteredEntries = useMemo(() => {
        const entriesFilteredRaw = (() => {
            let entriesToFilter = entriesToRender;
            if (dropdownInputValue) {
                entriesToFilter = entriesToRender
                    .filter(entry => (isString(entry.label)) && (entry.label.toLowerCase()
                        .search(dropdownInputValue.toLowerCase()) >= 0));
            }
            entriesToFilter = entriesToFilter
                .filter(entry => optionsFilterCallback?.({entry, entriesToRender}) !== false);

            return (sortComparator ? entriesToFilter.sort(sortComparator) : entriesToFilter);
        })();

        const entriesFilteredJSX = entriesFilteredRaw.map(entry => (
            <Select.Option
                key={formatFieldValueForSelect(entry.value)}
                value={formatFieldValueForSelect(entry.value)}
                className="select-field__option"
            >
                {mode === ECustomSelectMode.multiple && showSearch && typeof entry.label === 'string' ? (
                    <CustomSelectEntryHighlighter
                        text={entry.label}
                        search={dropdownInputValue}
                    />
                ) : optionsLabelFormatter ? optionsLabelFormatter(entry.label, entry.value) : entry.label}
            </Select.Option>
        ));

        return {
            entriesFilteredJSX,
            entriesFilteredRaw,
        };
    }, [dropdownInputValue, entriesToRender]);

    const multipleModeDropdownWithSearch = (menu: ReactElement) => (
        <SearchableDropdown
            menu={menu}
            inputValue={dropdownInputValue}
            setInputValue={setDropdownInputValue}
        />
    );

    const setDropdownSelectedIcon = (p: any) => (p?.isSelected ? <Checkbox checked />
        : <Checkbox checked={false} />);

    return {
        dropdownOpen,
        setDropdownOpen,
        dropdownInputValue,
        setDropdownInputValue,
        filterEntry,
        filteredEntries,
        setDropdownSelectedIcon,
        multipleModeDropdownWithSearch,
    };
};
